/**
 * <palm-canvas> — a palm photograph (or a plain diagram) with the mounts drawn as
 * a reference frame, and freehand tracing of each rekhā by the reader.
 *
 * ◆ **The photograph never leaves the page.** It is read from the file the reader
 * chose, drawn to a canvas, and forgotten; no network request is made. Drawing to
 * a canvas re-encodes only pixels, so EXIF — including any GPS position — is
 * dropped by construction rather than by a stripping step that could be missed.
 * The file input is never given a `capture` attribute that would silently send a
 * camera stream anywhere; the browser's own picker decides.
 *
 * ◆ **Correction, not oracle.** No line is detected here. The reader places the
 * mount reference by eye and traces the lines they see; the traced polylines are
 * measured (length, straightness) and shown as such. Automatic detection is a
 * later phase (src/hasta/), and when it lands it will draw into this same
 * canvas as editable polylines.
 *
 * Attributes: `hand` = left | right, `mounts` = present to show the mount frame.
 * Properties: `.activeLine` (a rekhā id), `.traces` (read-only copy).
 * Events: `trace-change` with `{ traces }`; `image-change` with `{ hasImage }`.
 *
 * © Manish Jagdish Thatte
 */

import { LINES, lineById, mountPositions } from '../hasta/reference.js';
import { h } from '../utils/dom.js';

const MAX_SIDE = 900;   // longest side of the working canvas, in pixels

export class PalmCanvas extends HTMLElement {
  static observedAttributes = ['hand', 'mounts'];

  connectedCallback() {
    if (this._built) return;
    this._built = true;
    this._image = null;
    this._traces = Object.fromEntries(LINES.map((l) => [l.id, []]));
    this._active = 'hridaya';
    this._drawing = null;

    this.canvas = h('canvas', {
      class: 'palm-canvas__canvas', width: 560, height: 700,
      role: 'img', tabindex: 0,
      'aria-label': 'Palm reference frame. Draw a line with a pointer or finger; use the buttons for keyboard access.'
    });
    this.append(this.canvas);

    this.canvas.addEventListener('pointerdown', (e) => this.begin(e));
    this.canvas.addEventListener('pointermove', (e) => this.extend(e));
    this.canvas.addEventListener('pointerup', (e) => this.finish(e));
    this.canvas.addEventListener('pointercancel', (e) => this.finish(e));
    this.paint();
  }

  attributeChangedCallback() { if (this._built) this.paint(); }

  get hand() { return this.getAttribute('hand') === 'right' ? 'right' : 'left'; }
  get showMounts() { return this.hasAttribute('mounts'); }

  get activeLine() { return this._active; }
  set activeLine(id) { lineById(id); this._active = id; }

  get traces() { return structuredClone(this._traces); }
  get hasImage() { return !!this._image; }

  /**
   * Load a photograph from a File. Resolves once it is drawn.
   * ⚠ The File and its object URL are released immediately after the pixels are
   * copied; nothing keeps the original.
   */
  async loadFile(file) {
    if (!file || !/^image\//.test(file.type)) throw new TypeError('Please choose an image file.');
    let source;
    // `imageOrientation: 'from-image'` applies the EXIF rotation to the pixels, so
    // a portrait phone photo is not drawn sideways — and then the metadata is gone.
    if (typeof createImageBitmap === 'function') {
      source = await createImageBitmap(file, { imageOrientation: 'from-image' });
    } else {
      source = await new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That file could not be read as an image.')); };
        img.src = url;
      });
    }
    const w = source.width, hgt = source.height;
    const scale = Math.min(1, MAX_SIDE / Math.max(w, hgt));
    const off = document.createElement('canvas');
    off.width = Math.round(w * scale);
    off.height = Math.round(hgt * scale);
    off.getContext('2d').drawImage(source, 0, 0, off.width, off.height);
    source.close?.();
    this._image = off;
    this.canvas.width = off.width;
    this.canvas.height = off.height;
    this.paint();
    this.dispatchEvent(new CustomEvent('image-change', { detail: { hasImage: true }, bubbles: true }));
  }

  clearImage() {
    this._image = null;
    this.canvas.width = 560;
    this.canvas.height = 700;
    this.paint();
    this.dispatchEvent(new CustomEvent('image-change', { detail: { hasImage: false }, bubbles: true }));
  }

  clearTrace(id = null) {
    for (const l of LINES) if (id === null || l.id === id) this._traces[l.id] = [];
    this.paint();
    this.emitTrace();
  }

  /** Measured facts about each traced line: how many strokes, how long, how straight. */
  measurements() {
    const out = {};
    for (const l of LINES) {
      const strokes = this._traces[l.id];
      if (!strokes.length) continue;
      const pts = strokes.flat();
      let length = 0;
      for (const s of strokes) for (let i = 1; i < s.length; i++) length += Math.hypot(s[i][0] - s[i - 1][0], s[i][1] - s[i - 1][1]);
      const first = strokes[0][0], last = strokes[strokes.length - 1].at(-1);
      const chord = Math.hypot(last[0] - first[0], last[1] - first[1]);
      out[l.id] = {
        strokes: strokes.length,
        points: pts.length,
        // Lengths are fractions of the canvas width, so they mean the same on any photo size.
        length,
        straightness: length > 0 ? Math.min(1, chord / length) : 0
      };
    }
    return out;
  }

  /* ── drawing input ──────────────────────────────────────────────────── */

  point(e) {
    const r = this.canvas.getBoundingClientRect();
    return [
      Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))
    ];
  }

  begin(e) {
    if (e.button !== undefined && e.button !== 0) return;
    this.canvas.setPointerCapture?.(e.pointerId);
    this._drawing = [this.point(e)];
    this._traces[this._active].push(this._drawing);
    e.preventDefault();
  }

  extend(e) {
    if (!this._drawing) return;
    const p = this.point(e);
    const last = this._drawing.at(-1);
    if (Math.hypot(p[0] - last[0], p[1] - last[1]) < 0.003) return;
    this._drawing.push(p);
    this.paint();
  }

  finish() {
    if (!this._drawing) return;
    if (this._drawing.length < 2) this._traces[this._active].pop();
    this._drawing = null;
    this.paint();
    this.emitTrace();
  }

  emitTrace() {
    this.dispatchEvent(new CustomEvent('trace-change', { detail: { traces: this.traces }, bubbles: true }));
  }

  /* ── painting ───────────────────────────────────────────────────────── */

  colourOf(line) {
    const v = getComputedStyle(document.documentElement).getPropertyValue(line.colour).trim();
    return v || line.fallback;
  }

  paint() {
    const c = this.canvas;
    if (!c) return;
    const ctx = c.getContext('2d');
    const W = c.width, H = c.height;
    ctx.clearRect(0, 0, W, H);

    if (this._image) {
      ctx.drawImage(this._image, 0, 0, W, H);
    } else {
      // A plain diagram, so the page is usable with no photograph at all.
      ctx.fillStyle = '#f0e3c9';
      ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = '#8d7b70';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect?.(W * 0.12, H * 0.18, W * 0.76, H * 0.74, 28);
      ctx.stroke();
      ctx.fillStyle = '#6b5a50';
      ctx.font = `${Math.round(W / 34)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText('Fingers at the top', W / 2, H * 0.10);
    }

    if (this.showMounts) this.paintMounts(ctx, W, H);

    for (const l of LINES) {
      const colour = this.colourOf(l);
      for (const stroke of this._traces[l.id]) this.paintStroke(ctx, stroke, W, H, colour);
    }
  }

  paintMounts(ctx, W, H) {
    const r = Math.max(14, W * 0.055);
    ctx.font = `${Math.round(W / 40)}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const m of mountPositions(this.hand)) {
      const x = m.x * W, y = m.y * H;
      // A pale halo under a dark ring: legible over a dark or a pale photograph.
      ctx.lineWidth = 5; ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.stroke();
      ctx.lineWidth = 2; ctx.strokeStyle = '#26324f';
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.stroke();
      ctx.lineWidth = 4; ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.strokeText(m.name.replace(/ \(.*/, ''), x, y + r + 12);
      ctx.fillStyle = '#26324f';
      ctx.fillText(m.name.replace(/ \(.*/, ''), x, y + r + 12);
    }
  }

  paintStroke(ctx, stroke, W, H, colour) {
    if (stroke.length < 2) return;
    const path = () => {
      ctx.beginPath();
      stroke.forEach(([x, y], i) => (i ? ctx.lineTo(x * W, y * H) : ctx.moveTo(x * W, y * H)));
    };
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    // The contrasting halo the palm stylesheet asks for: a stroke must read over both dark and pale skin.
    ctx.lineWidth = 7; ctx.strokeStyle = 'rgba(255,255,255,0.85)'; path(); ctx.stroke();
    ctx.lineWidth = 3.5; ctx.strokeStyle = colour; path(); ctx.stroke();
  }
}

customElements.define('palm-canvas', PalmCanvas);
